import { useMemo } from 'react';

import type * as api from '@gitbook/api-client';
import { dedupeBy } from '@gitbook/util-ts';

import { getSiteSpacesFromStructure } from '../../../state';
import { flattenSpacesInOrgAllContent } from '../../Organization';

export interface UseAvailableSiteSpacesProps {
    siteStructure: api.SiteStructure;
    orgAllContent: api.OrganizationAllContent;
}

export interface SiteSpacePickerOptions {
    /** Every linked space followed by organization spaces not yet linked to the site. */
    spaces: api.Space[];
    /** Spaces referenced by the site structure, including spaces missing from orgAllContent. */
    inSiteSpaces: api.Space[];
    /** Organization spaces that are not referenced by the site structure. */
    notInSiteSpaces: api.Space[];
}

/**
 * Build the candidate spaces shown by site-content pickers.
 *
 * The site structure is authoritative for linked spaces because orgAllContent can omit one.
 */
export function getSiteSpacePickerOptions(
    siteStructure: api.SiteStructure,
    orgAllContent: api.OrganizationAllContent
): SiteSpacePickerOptions {
    const inSiteSpaces = dedupeBy(
        getSiteSpacesFromStructure(siteStructure).map((siteSpace) => siteSpace.space),
        (space) => space.id
    );
    const linkedSpaceIds = new Set(inSiteSpaces.map((space) => space.id));
    const notInSiteSpaces = dedupeBy(
        flattenSpacesInOrgAllContent(orgAllContent).filter(
            (space) => !linkedSpaceIds.has(space.id)
        ),
        (space) => space.id
    );

    return {
        spaces: [...inSiteSpaces, ...notInSiteSpaces],
        inSiteSpaces,
        notInSiteSpaces,
    };
}

/**
 * Returns the available spaces for a site based on the site structure and organization content.
 */
export function getAvailableSiteSpaces(
    siteStructure: api.SiteStructure,
    orgAllContent: api.OrganizationAllContent
): api.Space[] {
    return getSiteSpacePickerOptions(siteStructure, orgAllContent).notInSiteSpaces;
}

/**
 * Returns the available spaces for a site based on the site structure and organization content.
 * It filters the spaces to only include those that are not part of the site structure.
 */
export function useAvailableSiteSpaces(props: UseAvailableSiteSpacesProps) {
    const { siteStructure, orgAllContent } = props;
    return useMemo(() => {
        return getAvailableSiteSpaces(siteStructure, orgAllContent);
    }, [siteStructure, orgAllContent]);
}
